<template>
    <ValidationObserver ref="profileForm" tag="article" v-slot="{invalid}" class="lg:col-span-9">
        <form @submit.prevent="saveData" method="post" class="py-6 px-4 space-y-6 sm:p-6 lg:pb-8">
            <div class="flex flex-col w-full justify-between">
                <h3>Profile</h3>
                <p>Member Since: {{user.createDate | formatDateTime}}</p>
            </div>

            <div class="flex flex-col space-y-6 lg:flex-row lg:space-y-0 lg:space-x-12">
                <div class="flex-grow space-y-6">
                    <ValidationProvider mode="aggressive" name="username" rules="required" v-slot="{errors, invalid, validated}" tag="div" class="space-y-1">
                        <label for="username" class="text-sm">Username</label>
                        <input id="username" class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey-disabled focus:outline-none focus:border-leonard-black-hover" :class="{'border-leonard-error': invalid && validated}" v-model="userData.username">
                        <p class="text-sm text-leonard-error mt-1">{{errors[0]}}</p>
                    </ValidationProvider>

                    <ValidationProvider mode="aggressive" name="emailAddress" rules="required|email" v-slot="{errors, invalid, validated}" tag="div" class="space-y-1">
                        <label for="email" class="text-sm">Email</label>
                        <input id="emailAddress" class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey-disabled focus:outline-none focus:border-leonard-black-hover" :class="{'border-leonard-error': invalid && validated}" v-model="userData.emailAddress">
                        <p class="text-sm text-leonard-error mt-1">{{errors[0]}}</p>
                    </ValidationProvider>
                </div>

                <div class="flex-grow space-y-1 lg:flex-grow-0 lg:flex-shrink-0">
                    <p class="text-sm md:text-center" aria-hidden="true">Photo</p>
                    <div class="lg:hidden">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 inline-block rounded-full overflow-hidden h-12 w-12" aria-hidden="true">
                                <img class="h-full w-full" :src="'https://api.dicebear.com/9.x/initials/svg?seed='+initials(userData)+'&scale=70&radius=50&backgroundColor=B2B7BB'" alt="">
                            </div>
                            <div class="ml-5 rounded-md shadow-sm">
                                <div class="group relative border border-gray-300 rounded-md py-2 px-3 flex items-center justify-center focus-within:border-blue-300 focus-within:shadow-outline-blue focus-within:text-gray-900 transition duration-150 ease-in-out">
                                    <label for="user_photo" class="relative text-sm leading-4 font-medium text-gray-700 group-hover:text-gray-500 transition duration-150 ease-in-out pointer-events-none">
                                        <span>Change</span>
                                        <span class="sr-only"> user photo</span>
                                    </label>
                                    <input id="userImageAlt" type="file" accept="image/*" class="absolute w-full h-full opacity-0 cursor-pointer">
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="hidden relative rounded-full overflow-hidden lg:block transition duration-150 ease-in-out">
                        <img class="relative w-32 h-32" :src="'https://api.dicebear.com/9.x/initials/svg?seed='+initials(userData)+'&scale=70&radius=50&backgroundColor=B2B7BB'" alt="">
                        <label class="absolute inset-0 w-full h-full bg-black bg-opacity-75 flex items-center justify-center text-sm leading-5 font-medium text-white opacity-0 hover:opacity-100 focus-within:opacity-100 transition duration-150 ease-in-out">
                            <span>Change</span>
                            <span class="sr-only"> user photo</span>
                            <input id="userImage" type="file" accept="image/*" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer">
                        </label>
                    </div>
                </div>
            </div>

            <div class="grid md:grid-2 md:grid-flow-col gap-2 w-full mb-3">
                <ValidationProvider mode="aggressive" name="first_name" rules="required" v-slot="{errors, invalid, validated}" tag="div">
                    <label for="first_name" class="text-sm">First Name <span class="text-leonard-disclaimer">(Given Name)</span></label>
                    <input id="first_name" class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey-disabled focus:outline-none focus:border-leonard-black-hover" :class="{'border-leonard-error': invalid && validated}" v-model="userData.FirstName">
                    <p class="text-sm text-leonard-error mt-1">{{errors[0]}}</p>
                </ValidationProvider>

                <ValidationProvider  mode="aggressive" name="last_name" rules="required" v-slot="{errors, invalid, validated}" tag="div">
                    <label for="last_name" class="text-sm">Last Name <span class="text-leonard-disclaimer">(Surname/Family Name)</span></label>
                    <input id="last_name" class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey-disabled focus:outline-none focus:border-leonard-black-hover" :class="{'border-leonard-error': invalid && validated}" v-model="userData.LastName">
                    <p class="text-sm text-leonard-error mt-1">{{errors[0]}}</p>
                </ValidationProvider>
            </div>

            <div class="flex items-center justify-end space-x-5">
                <p v-if="showChangesMade" class="text-leonard-warning">Changes have been made!</p>
                <button v-if="showChangesMade"
                        type="button"
                        class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-transparent border-leonard-green text-leonard-green hover:bg-leonard-green-hover hover:border-transparent hover:text-leonard-white focus:ring-leonard-green cursor-pointer"
                        @click="cancel"
                >
                    Cancel
                </button>
                <button type="submit"
                        class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-transparent text-leonard-white hover:bg-leonard-green-hover focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed"
                        :disabled="invalid || !showChangesMade"
                >
                    Save
                </button>
            </div>
        </form>
    </ValidationObserver>
</template>

<script>
import {lightFormat, parse} from 'date-fns'
import { ValidationProvider, ValidationObserver, extend} from 'vee-validate'

import User from '../../classes/User'

export default {
    props: ['user'],

    components: {
        ValidationProvider,
        ValidationObserver,
    },

    data() {
        return {
            User: new User(),
            userData: {...this.user},
            showChangesMade: false,
        }
    },

    mounted() {
    },

    methods: {
        saveData() {
            let _this = this
            let data = {
                user_id: this.userData.id,
                FirstName: this.userData.FirstName,
                LastName: this.userData.LastName,
                emailAddress: this.userData.emailAddress,
                username: this.userData.username
            }

            this.User.saveProfile(data).then(function(response) {
                if(!response.success) {
                    _this.$refs.profileForm.setErrors(response.data.errors)
                    return
                }

                // update local stuff
                _this.updateUser();
                feedback('Profile Saved', 'success')
            })
        },

        updateUser() {
            this.$emit('updated', this.userData)
        },

        initials(userData) {
            let letters = ''
            if(userData.FirstName && userData.LastName) {
                letters = userData.FirstName.charAt(0) + userData.LastName.charAt(0)
            } else {
                letters = userData.emailAddress
            }
            return letters
        },

        changesWereMade() {
            this.showChangesMade = true
        },

        resetChanges() {
            this.showChangesMade = false
        },

        cancel() {
            this.userData = {...this.user}
        },

        propertiesAreEqual(changedData) {
            let isEqual = true,
                fields = ['username', 'FirstName', 'LastName', 'emailAddress']

            for (const property in changedData) {
                if(fields.includes(property) && Object.keys(this.user).includes(property)) {
                    if(changedData[property] !== this.user[property]) {
                        // console.log('Property Not Equal', [property, changedData[property], this.user[property]])
                        isEqual = false
                    } else {
                        this.resetChanges()
                    }
                }
            }

            return isEqual
        }
    },

    filters: {
        formatDateTime: function(str) {
            let date = parse(str, 'yyyy-MM-dd HH:mm:ss', new Date())
            return lightFormat(date, 'MM/dd/yyyy')
        }
    },

    watch: {
        userData: {
            handler: function(newVal, oldVal) {
                if(!this.propertiesAreEqual(newVal)) {
                    this.changesWereMade()
                }
            },
            deep: true,
        }
    }
}
</script>
